import React, { useState, useEffect, useRef } from 'react';
// import ReactMarkdown from 'react-markdown'; // Ya no se usa
// import remarkBreaks from 'remark-breaks'; // Ya no se usa

import { 
    BookOpen, HelpCircle, Check, X, RefreshCw, Eye, 
    Timer as TimerIcon, ArrowLeft, ArrowRight, Sparkles,
    Play, StopCircle
} from 'lucide-react';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import './Exercise.css';
import './ReadingExercise.css';

// --- 1. CIRUGÍA AL RENDERIZADOR (CORREGIDA) ---
const GuidedReaderRenderer = ({ text }) => {
    // ESTA LÍNEA ESTÁ CORREGIDA (usa la lógica de Spacy)
    const chunks = (text || '').match(/[^.!?]+[.!?]+|\S+/g) || [];
    
    return (
        <React.Fragment>
            {chunks.map((chunk, index) => (
                <div key={index} id={`reading-phrase-${index}`} className="reading-phrase">
                    {chunk.split(/(\*\*.*?\*\*)/g).map((part, partIndex) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            return (
                                <strong key={partIndex} className="re-highlight-text">
                                    {part.slice(2, -2)}
                                </strong>
                            );
                        }
                        return part; // Parte de texto normal
                    })}
                </div>
            ))}
        </React.Fragment>
    );
};


function ReadingExercise({ exerciseData, onCorrectAnswer }) {
    const { title, subtitle, text, comprehension_quiz } = exerciseData || {};
    const questions = comprehension_quiz?.questions || [];

    const [currentIndex, setCurrentIndex] = useState(0);
    const [userChoices, setUserChoices] = useState(Array(questions.length).fill(null));
    const [answeredCount, setAnsweredCount] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [timeLeft, setTimeLeft] = useState(300);
    const [showHint, setShowHint] = useState(false);

    const [isSpeaking, setIsSpeaking] = useState(false);
    const isSpeakingRef = useRef(false);

    const passageRef = useRef(null); 

    useEffect(() => {
        return () => {
            if (window.speechSynthesis && window.speechSynthesis.speaking) {
                window.speechSynthesis.cancel();
            }
        };
    }, []);

    const handleStop = () => {
        window.speechSynthesis.cancel();
        isSpeakingRef.current = false;
        setIsSpeaking(false);
        document.querySelectorAll('.reading-phrase.active, .reading-phrase.passed').forEach(el => {
            el.classList.remove('active', 'passed');
        });
    };

    // --- 3. CIRUGÍA A LA LÓGICA DE AUDIO (CORREGIDA) ---
    const handlePlay = (textToSpeak) => {
        if (isSpeakingRef.current || !textToSpeak) return;
        
        handleStop(); // Limpia todo

        const cleanText = textToSpeak.replace(/[*_]/g, '');
        
        // ESTA LÍNEA ESTÁ CORREGIDA (usa la lógica de Spacy)
        const chunks = cleanText.match(/[^.!?]+[.!?]+|\S+/g) || [];
        
        const passageElement = passageRef.current; // El div con scroll
        if (!passageElement) return;

        const speakChunk = (chunkIndex) => {
            if (chunkIndex >= chunks.length) {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                return;
            }

            const chunk = chunks[chunkIndex];
            const utterance = new SpeechSynthesisUtterance(chunk);
            utterance.lang = 'en-US';
            
            const phraseElement = document.getElementById(`reading-phrase-${chunkIndex}`);

            utterance.onstart = () => {
                if (chunkIndex === 0) {
                    isSpeakingRef.current = true;
                    setIsSpeaking(true);
                }
                
                if (phraseElement) {
                    phraseElement.classList.add('active');

                    // Lógica del Scroll (está perfecta)
                    const parentRect = passageElement.getBoundingClientRect();
                    const childRect = phraseElement.getBoundingClientRect();
                    const scrollOffset = (childRect.top + passageElement.scrollTop) - parentRect.top - (parentRect.height / 2) + (childRect.height / 2);
                    
                    passageElement.scrollTo({
                        top: scrollOffset,
                        behavior: 'smooth'
                    });
                }
            };

            utterance.onend = () => {
                if (phraseElement) {
                    phraseElement.classList.remove('active');
                    phraseElement.classList.add('passed');
                }
                speakChunk(chunkIndex + 1);
            };

            utterance.onerror = (e) => {
                console.error("Error en SpeechSynthesis:", e);
                isSpeakingRef.current = false;
                setIsSpeaking(false);
            };

            window.speechSynthesis.speak(utterance);
        };

        speakChunk(0);
    };

    // --- (Lógica del Quiz - Sin cambios) ---

    const restartExercise = () => {
        handleStop(); // Detiene el audio
        setCurrentIndex(0);
        setUserChoices(Array(questions.length).fill(null));
        setAnsweredCount(0);
        setCorrectCount(0);
        setTimeLeft(300);
        setShowHint(false);
    };

    useEffect(() => {
        if (timeLeft <= 0 || answeredCount === questions.length) return;
        const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
        return () => clearInterval(timer);
    }, [timeLeft, answeredCount, questions.length]);

    const currentQuestion = questions[currentIndex];
    const isAnswered = userChoices[currentIndex] !== null;
    const isChoiceCorrect = isAnswered && currentQuestion && userChoices[currentIndex] === currentQuestion.answer;

    const handleAnswer = (choice) => {
        if (isAnswered) return;
        const newUserChoices = [...userChoices];
        newUserChoices[currentIndex] = choice;
        setUserChoices(newUserChoices);
        
        if (userChoices[currentIndex] === null) {
            setAnsweredCount(prev => prev + 1);
            if (choice === currentQuestion.answer) {
                setCorrectCount(prev => prev + 1);
            }
        }
    };
    
    const handleNext = () => {
        handleStop(); // Detiene el audio
        setShowHint(false);
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            onCorrectAnswer();
            showAchievement('¡Comprensión lectora completada!', '📚');
            launchConfetti();
        }
    };

    const handlePrevious = () => {
        handleStop(); // Detiene el audio
        setShowHint(false);
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    if (!questions || questions.length === 0) {
        return <div className="exercise-container">No se encontraron preguntas para este ejercicio.</div>;
    }
    
    const precision = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;
    const progressPercentage = questions.length > 0 ? ((answeredCount / questions.length) * 100) : 0;

    // --- (JSX - Sin cambios) ---
    return (
        <div className="exercise-container">
            <header className="re-header">
                <div className="re-icon-container-inset">
                    <BookOpen size={24} />
                </div>
                <div className="re-header-card">
                    <h2 className="main-title">{title || 'Reading Comprehension'}</h2>
                    <p className="main-subtitle">{subtitle || 'Answer the questions to check your understanding.'}</p>
                </div>
            </header>
            
            <div className="re-controls">
                <div className="re-stat-box-inset"><TimerIcon size={18} /> {Math.floor(timeLeft/60)}:{(timeLeft%60).toString().padStart(2, '0')}</div>
                <button className="re-control-btn" onClick={() => setShowHint(!showHint)}><Eye size={18} /></button>
                <button className="re-control-btn" onClick={restartExercise}><RefreshCw size={18} /></button>
            </div>

            <div className="re-progress-bar-container">
                <div className="re-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
            </div>
            <div className="re-stats-container">
                <div className="re-stat-box-inset large"><div className="re-stat-number">{currentIndex + 1}/{questions.length}</div><div className="re-stat-label">Pregunta</div></div>
                <div className="re-stat-box-inset large"><div className="re-stat-number">{answeredCount}/{questions.length}</div><div className="re-stat-label">Respondidas</div></div>
                <div className="re-stat-box-inset large"><div className="re-stat-number">{precision}%</div><div className="re-stat-label">Precisión</div></div>
            </div>

            <section className="re-context-card">
                <div className="re-card-header">
                    <BookOpen size={24} className="main-icon" />
                    <h3 className="re-content-title">Contexto de Lectura</h3>
                </div>
                
                <div className="re-reading-passage" ref={passageRef}>
                    <GuidedReaderRenderer text={text || ''} />
                </div>

                <div className="re-player-controls">
                    {isSpeaking ? (
                        <button className="re-player-btn" onClick={handleStop} title="Stop">
                            <StopCircle size={24} />
                        </button>
                    ) : (
                        <button className="re-player-btn" onClick={() => handlePlay(text)} title="Play">
                            <Play size={24} />
                        </button>
                    )}
                </div>
            </section>

            <main className="re-comprehension-card">
                <div className="re-card-header">
                    <Sparkles size={24} className="main-icon" />
                    <h3 className="re-content-title">Comprensión Lectora</h3>
                </div>
                <p className="re-content-instruction">{comprehension_quiz?.instruction}</p>
                
                <div className="re-quiz-question-wrapper">
                    <p className="re-question-text">{currentQuestion?.question}</p>
                </div>
                
                <div className="re-quiz-options">
                    {currentQuestion?.options.map((option, index) => {
                        const isSelected = userChoices[currentIndex] === option;
                        let optionClass = '';
                        if (isAnswered) {
                            if (option === currentQuestion.answer) optionClass = 'correct';
                            else if (isSelected) optionClass = 'incorrect';
                        }
                        return (
                            <button key={index} className={`re-quiz-option ${optionClass}`} onClick={() => handleAnswer(option)} disabled={isAnswered}>
                                <span className="re-option-letter-badge">{String.fromCharCode(65 + index)}</span>
                                <span>{option}</span>
                            </button>
                        )
                    })}
                </div>

                {isAnswered && (
                    <div className={`re-feedback-box ${isChoiceCorrect ? 'correct' : 'incorrect'}`}>
                        <div className="re-feedback-title">
                            {isChoiceCorrect ? <Check size={18}/> : <X size={18}/>}
                            {isChoiceCorrect ? '¡Excelente trabajo!' : 'Incorrecto'}
                        </div>
                        <p className="re-feedback-explanation">{currentQuestion?.explanation}</p>
                    </div>
                )}
                {showHint && currentQuestion?.hint && !isAnswered && (
                    <div className="re-feedback-box hint">
                        <div className="re-feedback-title"><HelpCircle size={18}/> Pista</div>
                        <p className="re-feedback-explanation">{currentQuestion.hint}</p>
                    </div>
                )}
            </main>

            <footer className="re-navigation-controls">
                <button className="exercise-action-button" onClick={handlePrevious} disabled={currentIndex === 0}>
                    <ArrowLeft size={20} /> Back
                </button>
                <div className="re-progress-dots">
                    {questions.map((_, index) => (
                        <div key={index} className={`re-dot ${index === currentIndex ? 'active' : ''} ${userChoices[index] !== null ? 'answered' : ''}`}></div>
                    ))}
                </div>
                <button className="exercise-action-button" onClick={handleNext} disabled={!isAnswered}>
                    {currentIndex === questions.length - 1 ? 'Finalizar' : 'Next'} <ArrowRight size={20} />
                </button>
            </footer>
        </div>
    );
}

export default ReadingExercise;